import { useEffect, useState, useCallback, useRef } from "react";
import api, { errMsg, fmtQty, downloadReport } from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Empty } from "../components/common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Plus, Download, Upload, Pencil, FileSpreadsheet, Trash2 } from "lucide-react";
import { ItemImagePanel } from "../components/ItemImagePanel";
import { Checkbox } from "../components/ui/checkbox";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";

const EMPTY = { item_number: "", description: "", part_number: "", category: "", uom_primary: "EA", uom_secondary: "", conversion_factor: 1, lot_controlled: false, serial_controlled: false, standard_cost: 0, min_stock: 0, reorder_point: 0, max_stock: 0, preferred_supplier_id: "", family_id: "", group_id: "", subgroup_id: "", active: true };

export default function Items() {
  const { t } = useI18n();
  const { can } = useAuth();
  const [rows, setRows] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [families, setFamilies] = useState([]);
  const [groups, setGroups] = useState([]);
  const [subgroups, setSubgroups] = useState([]);
  const [stockMap, setStockMap] = useState({});
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [selected, setSelected] = useState([]);
  const [confirm, setConfirm] = useState(null); // { ids: [], label: "" }
  const [pnDup, setPnDup] = useState(null);

  useEffect(() => {
    const pn = (form.part_number || "").trim();
    if (!open || !pn) { setPnDup(null); return; }
    const h = setTimeout(() => {
      api.get("/items/part-number-check", { params: { pn, exclude_id: editId || "" } })
        .then((r) => setPnDup(r.data.exists ? r.data : null)).catch(() => setPnDup(null));
    }, 350);
    return () => clearTimeout(h);
  }, [form.part_number, open, editId]);
  const isAdmin = can("admin");

  const load = useCallback(() => {
    api.get("/items", { params: q ? { q } : {} }).then((r) => setRows(r.data));
    api.get("/items/stock-map").then((r) => setStockMap(r.data)).catch(() => setStockMap({}));
  }, [q]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/address-book", { params: { type: "supplier" } }).then((r) => setSuppliers(r.data));
    api.get("/classifications", { params: { level: "family" } }).then((r) => setFamilies(r.data));
  }, []);
  useEffect(() => {
    if (form.family_id) {
      api.get("/classifications", { params: { level: "group", parent_id: form.family_id } })
        .then((r) => setGroups(r.data));
    } else { setGroups([]); }
  }, [form.family_id]);
  useEffect(() => {
    if (form.group_id) {
      api.get("/classifications", { params: { level: "subgroup", parent_id: form.group_id } })
        .then((r) => setSubgroups(r.data));
    } else { setSubgroups([]); }
  }, [form.group_id]);

  const previewCode = (() => {
    const fam = families.find((f) => f.id === form.family_id);
    const grp = groups.find((g) => g.id === form.group_id);
    const sub = subgroups.find((s) => s.id === form.subgroup_id);
    if (fam && grp && sub) return `${fam.code}-${grp.code}-${sub.code}-####`;
    if (fam && grp) return `${fam.code}-${grp.code}-???-####`;
    if (fam) return `${fam.code}-???-???-####`;
    return "";
  })();

  const save = async () => {
    try {
      const payload = { ...form,
        conversion_factor: Number(form.conversion_factor),
        standard_cost: Number(form.standard_cost) || 0,
        min_stock: Number(form.min_stock) || 0,
        reorder_point: Number(form.reorder_point) || 0,
        max_stock: Number(form.max_stock) || 0 };
      if (editId) await api.put(`/items/${editId}`, payload);
      else await api.post("/items", payload);
      toast.success(t("common.saved"));
      setOpen(false); setForm(EMPTY); setEditId(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const exportCatalog = async () => {
    try { await downloadReport("/items/export", `catalogo_${Date.now()}.xlsx`); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const fileRef = useRef(null);
  const downloadTemplate = async () => {
    try { await downloadReport("/items-template-xlsx", "plantilla_articulos.xlsx"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const importFile = async (file) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/items-import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("it.importDone").replace("{n}", r.data.created));
      if (r.data.errors?.length) toast.error(r.data.errors.slice(0, 3).join(" · "));
      load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { if (fileRef.current) fileRef.current.value = ""; }
  };

  const editable = can("admin", "buyer", "warehouse");
  const canCreateNew = !editId; // classifications only for new items

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const allSelected = rows.length > 0 && rows.every((r) => selected.includes(r.id));
  const toggleAll = () => setSelected(allSelected ? [] : rows.map((r) => r.id));

  const doDelete = async () => {
    const { ids } = confirm;
    setConfirm(null);
    try {
      if (ids.length === 1) {
        const r = await api.delete(`/items/${ids[0]}`);
        toast.success(`${t("it.deleted")} · ${r.data.deleted}`);
      } else {
        const r = await api.post("/items/bulk-delete", { ids });
        toast.success(t("it.bulkDeleted").replace("{n}", r.data.deleted.length));
        r.data.blocked.slice(0, 3).forEach((b) => toast.error(`${b.item_number}: ${b.reason}`));
      }
      setSelected([]);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="items-page">
      <PageHeader title={t("it.title")} subtitle={t("it.subtitle")}>
        <Button variant="outline" size="sm" className="rounded-none text-xs" onClick={exportCatalog} data-testid="items-export-button">
          <FileSpreadsheet size={13} className="mr-1" /> {t("it.export")}
        </Button>
        {editable && (
          <>
            <input ref={fileRef} type="file" accept=".csv,.xlsx" className="hidden"
              onChange={(e) => importFile(e.target.files?.[0])} data-testid="items-import-input" />
            <Button variant="outline" size="sm" onClick={downloadTemplate}
              className="rounded-none text-xs font-bold" data-testid="items-template-button">
              <Download size={13} className="mr-1" /> {t("it.template")}
            </Button>
            <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()}
              className="rounded-none text-xs font-bold" data-testid="items-import-button">
              <Upload size={13} className="mr-1" /> {t("it.import")}
            </Button>
          </>
        )}
        {editable && (
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setForm(EMPTY); setEditId(null); } }}>
            <DialogTrigger asChild>
              <Button size="sm" className="rounded-none text-xs font-bold" data-testid="items-new-button">
                <Plus size={13} className="mr-1" /> {t("it.new")}
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-none sm:max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="uppercase font-black">{editId ? t("common.edit") : t("it.new")}</DialogTitle>
                <DialogDescription className="text-xs">{t("it.subtitle")}</DialogDescription>
              </DialogHeader>

              {/* Classification cascade (only for new items) */}
              {canCreateNew && (
                <div className="border p-3 bg-muted/30">
                  <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-2">
                    {t("it.classification")}
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div className="space-y-1">
                      <Label className="text-[10px] font-bold uppercase tracking-widest">{t("cls.family")}</Label>
                      <Select value={form.family_id || "none"} onValueChange={(v) => setForm({ ...form, family_id: v === "none" ? "" : v, group_id: "", subgroup_id: "" })}>
                        <SelectTrigger className="rounded-none h-8 text-xs" data-testid="item-form-family"><SelectValue placeholder="—" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">—</SelectItem>
                          {families.map((f) => <SelectItem key={f.id} value={f.id}>{f.code} — {f.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-[10px] font-bold uppercase tracking-widest">{t("cls.group")}</Label>
                      <Select value={form.group_id || "none"} onValueChange={(v) => setForm({ ...form, group_id: v === "none" ? "" : v, subgroup_id: "" })} disabled={!form.family_id}>
                        <SelectTrigger className="rounded-none h-8 text-xs" data-testid="item-form-group"><SelectValue placeholder="—" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">—</SelectItem>
                          {groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.code} — {g.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-[10px] font-bold uppercase tracking-widest">{t("cls.subgroup")}</Label>
                      <Select value={form.subgroup_id || "none"} onValueChange={(v) => setForm({ ...form, subgroup_id: v === "none" ? "" : v })} disabled={!form.group_id}>
                        <SelectTrigger className="rounded-none h-8 text-xs" data-testid="item-form-subgroup"><SelectValue placeholder="—" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">—</SelectItem>
                          {subgroups.map((s) => <SelectItem key={s.id} value={s.id}>{s.code} — {s.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  {previewCode && (
                    <div className="text-[11px] mt-2 text-muted-foreground">
                      {t("it.autoCode")}: <span className="font-black text-primary tabular-nums" data-testid="item-code-preview">{previewCode}</span>
                    </div>
                  )}
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                {editId && (
                  <div className="space-y-1 col-span-2">
                    <Label className="text-[10px] font-bold uppercase tracking-widest">{t("it.number")}</Label>
                    <Input value={form.item_number} disabled className="rounded-none h-8 text-xs font-black" data-testid="item-form-item-number" />
                  </div>
                )}
                <div className="col-span-2 space-y-1">
                  <Label className="text-[10px] font-bold uppercase tracking-widest">{t("common.description")}</Label>
                  <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-none h-8 text-xs" data-testid="item-form-description" />
                </div>
                <div className="col-span-2 space-y-1">
                  <Label className="text-[10px] font-bold uppercase tracking-widest">{t("it.partNumber")} <span className="text-muted-foreground normal-case">· {t("it.partNumberHint")}</span></Label>
                  <Input value={form.part_number} onChange={(e) => setForm({ ...form, part_number: e.target.value })}
                    className={`rounded-none h-8 text-xs font-mono ${pnDup ? "border-destructive" : ""}`} placeholder="1R-0739" data-testid="item-form-part-number" />
                  {pnDup && (
                    <div className="text-[11px] text-destructive font-bold" data-testid="item-form-part-number-dup">
                      {t("it.partNumberDup").replace("{x}", `${pnDup.item_number} · ${pnDup.description}`)}
                    </div>
                  )}
                </div>
                <div className="space-y-1"><Label className="text-[10px] font-bold uppercase tracking-widest">{t("it.uomP")}</Label><Input value={form.uom_primary} onChange={(e) => setForm({ ...form, uom_primary: e.target.value })} className="rounded-none h-8 text-xs" data-testid="item-form-uom-primary" /></div>
                <div className="space-y-1"><Label className="text-[10px] font-bold uppercase tracking-widest">{t("it.uomS")}</Label><Input value={form.uom_secondary} onChange={(e) => setForm({ ...form, uom_secondary: e.target.value })} className="rounded-none h-8 text-xs" data-testid="item-form-uom-secondary" /></div>
                <div className="space-y-1"><Label className="text-[10px] font-bold uppercase tracking-widest">{t("it.factor")}</Label><Input type="number" value={form.conversion_factor} onChange={(e) => setForm({ ...form, conversion_factor: e.target.value })} className="rounded-none h-8 text-xs" data-testid="item-form-factor" /></div>
                <div className="space-y-1"><Label className="text-[10px] font-bold uppercase tracking-widest">{t("it.minStock")}</Label><Input type="number" min="0" step="0.01" value={form.min_stock} onChange={(e) => setForm({ ...form, min_stock: e.target.value })} className="rounded-none h-8 text-xs" data-testid="item-form-min-stock" /></div>
                <div className="space-y-1"><Label className="text-[10px] font-bold uppercase tracking-widest">{t("it.reorderPoint")}</Label><Input type="number" min="0" step="0.01" value={form.reorder_point} onChange={(e) => setForm({ ...form, reorder_point: e.target.value })} className="rounded-none h-8 text-xs" data-testid="item-form-reorder-point" /></div>
                <div className="space-y-1"><Label className="text-[10px] font-bold uppercase tracking-widest">{t("it.maxStock")}</Label><Input type="number" min="0" step="0.01" value={form.max_stock} onChange={(e) => setForm({ ...form, max_stock: e.target.value })} className="rounded-none h-8 text-xs" data-testid="item-form-max-stock" /></div>
                {can("admin", "buyer") && (
                  <div className="space-y-1 col-span-2 border-t pt-3">
                    <Label className="text-[10px] font-bold uppercase tracking-widest">{t("it.stdCost")} <span className="text-muted-foreground normal-case">· {t("it.stdCostHint")}</span></Label>
                    <Input type="number" min="0" step="0.01" value={form.standard_cost} onChange={(e) => setForm({ ...form, standard_cost: e.target.value })}
                      className="rounded-none h-8 text-xs" placeholder="0.00" data-testid="item-form-std-cost" />
                  </div>
                )}
                <div className="col-span-2 text-[10px] text-muted-foreground -mt-1">{t("it.stockPolicyHelp")}</div>
                <div className="space-y-1 col-span-2">
                  <Label className="text-[10px] font-bold uppercase tracking-widest">{t("it.supplier")}</Label>
                  <Select value={form.preferred_supplier_id || "none"} onValueChange={(v) => setForm({ ...form, preferred_supplier_id: v === "none" ? "" : v })}>
                    <SelectTrigger className="rounded-none h-8 text-xs" data-testid="item-form-supplier"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">—</SelectItem>
                      {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2 pt-2 col-span-2">
                  <Switch checked={form.lot_controlled} onCheckedChange={(v) => setForm({ ...form, lot_controlled: v })} data-testid="item-form-lot" />
                  <Label className="text-[10px] font-bold uppercase tracking-widest">{t("it.lot")}</Label>
                  <Switch checked={form.serial_controlled} onCheckedChange={(v) => setForm({ ...form, serial_controlled: v })} data-testid="item-form-serial" />
                  <Label className="text-[10px] font-bold uppercase tracking-widest">{t("sn.controlled")}</Label>
                </div>
                {editId && <ItemImagePanel itemId={editId} t={t} />}
              </div>
              <Button onClick={save} disabled={!form.description || !!pnDup || (canCreateNew && !(form.family_id && form.group_id && form.subgroup_id))}
                className="rounded-none font-bold" data-testid="item-save-button">
                {t("common.save")}
              </Button>
            </DialogContent>
          </Dialog>
        )}
      </PageHeader>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <Input placeholder={t("common.search")} value={q} onChange={(e) => setQ(e.target.value)}
          className="rounded-none h-8 text-xs max-w-64" data-testid="items-search-input" />
        {isAdmin && selected.length > 0 && (
          <div className="flex items-center gap-2 border px-3 h-8 bg-muted/40" data-testid="items-bulk-bar">
            <span className="text-[11px] font-bold tabular-nums">{t("it.selected").replace("{n}", selected.length)}</span>
            <Button size="sm" variant="destructive" className="rounded-none h-6 text-[11px] font-bold"
              onClick={() => setConfirm({ ids: selected, label: t("it.selected").replace("{n}", selected.length) })} data-testid="items-bulk-delete-button">
              <Trash2 size={12} className="mr-1" /> {t("common.delete")}
            </Button>
            <button className="text-[11px] underline text-muted-foreground" onClick={() => setSelected([])} data-testid="items-clear-selection">{t("common.cancel")}</button>
          </div>
        )}
      </div>

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent className="rounded-none">
          <AlertDialogHeader>
            <AlertDialogTitle className="uppercase font-black">{t("it.deleteTitle")}</AlertDialogTitle>
            <AlertDialogDescription className="text-xs">{t("it.deleteDesc").replace("{x}", confirm?.label || "")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none text-xs" data-testid="items-delete-cancel">{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction className="rounded-none text-xs font-bold bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={doDelete} data-testid="items-delete-confirm">{t("common.delete")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {rows.length === 0 ? <Empty text={t("common.noData")} /> : (
        <div className="tbl-wrap" data-testid="items-table">
          <table className="tbl">
            <thead><tr>
              {isAdmin && <th className="w-8"><Checkbox checked={allSelected} onCheckedChange={toggleAll} data-testid="items-select-all" /></th>}
              <th>{t("it.stockLight")}</th>
              <th>{t("it.number")}</th><th>{t("common.description")}</th><th>{t("it.partNumber")}</th><th>{t("it.category")}</th>
              <th>{t("it.uomP")}</th><th>{t("it.uomS")}</th><th className="num">{t("it.factor")}</th>
              <th>{t("it.lot")}</th>
              <th className="num">{t("it.minStock")}</th>
              <th className="num">{t("it.reorderPoint")}</th>
              <th className="num">{t("it.maxStock")}</th>
              <th></th>
            </tr></thead>
            <tbody>
              {rows.map((r) => {
                const sm = stockMap[r.item_number] || { status: "grey", on_hand: 0 };
                const dot = { red: "bg-red-500", yellow: "bg-amber-400", green: "bg-emerald-500", grey: "bg-slate-300" }[sm.status];
                return (
                <tr key={r.id} className={selected.includes(r.id) ? "bg-destructive/5" : ""}>
                  {isAdmin && <td><Checkbox checked={selected.includes(r.id)} onCheckedChange={() => toggle(r.id)} data-testid={`item-select-${r.item_number}`} /></td>}
                  <td>
                    <div className="flex items-center gap-1.5" title={`${t(`it.light.${sm.status}`)} · ${t("rb.onHand")}: ${sm.on_hand}`} data-testid={`item-light-${r.item_number}`}>
                      <span className={`inline-block w-2.5 h-2.5 rounded-full ${dot}`}></span>
                      <span className="text-[10px] tabular-nums text-muted-foreground">{sm.on_hand}</span>
                    </div>
                  </td>
                  <td className="font-bold tabular-nums">{r.item_number}</td>
                  <td>{r.description}</td>
                  <td className="font-mono text-[11px]" data-testid={`item-pn-${r.item_number}`}>{r.part_number || "—"}</td>
                  <td className="text-muted-foreground text-[11px]">{r.category}</td>
                  <td>{r.uom_primary}</td><td>{r.uom_secondary || "—"}</td>
                  <td className="num">{fmtQty(r.conversion_factor)}</td>
                  <td>{r.lot_controlled ? <span className="text-[10px] font-bold text-primary">LOT</span> : ""}{r.serial_controlled ? <span className="text-[10px] font-bold text-primary"> SN</span> : ""}{!r.lot_controlled && !r.serial_controlled ? "—" : ""}</td>
                  <td className="num">{fmtQty(r.min_stock)}</td>
                  <td className="num">{fmtQty(r.reorder_point)}</td>
                  <td className="num">{fmtQty(r.max_stock)}</td>
                  <td>
                    {editable && (
                      <button data-testid={`item-edit-${r.item_number}`} className="text-muted-foreground hover:text-primary"
                        onClick={() => {
                          setForm({ item_number: r.item_number, description: r.description, part_number: r.part_number || "", category: r.category, uom_primary: r.uom_primary, uom_secondary: r.uom_secondary, conversion_factor: r.conversion_factor, lot_controlled: r.lot_controlled, serial_controlled: r.serial_controlled || false, standard_cost: r.standard_cost || 0, min_stock: r.min_stock || 0, reorder_point: r.reorder_point || 0, max_stock: r.max_stock || 0, preferred_supplier_id: r.preferred_supplier_id || "", family_id: r.family_id || "", group_id: r.group_id || "", subgroup_id: r.subgroup_id || "", active: r.active });
                          setEditId(r.id); setOpen(true);
                        }}>
                        <Pencil size={13} />
                      </button>
                    )}
                    {isAdmin && (
                      <button data-testid={`item-delete-${r.item_number}`} className="text-muted-foreground hover:text-destructive ml-2"
                        title={t("common.delete")} onClick={() => setConfirm({ ids: [r.id], label: `${r.item_number} · ${r.description}` })}>
                        <Trash2 size={13} />
                      </button>
                    )}
                  </td>
                </tr>
              )})}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

